import type { CSSProperties } from 'react';
import { getAvatarColorsFromName, getInitialsFromName } from '@repo/utils';

export interface UserAvatarProps {
  /** Full user name (e.g. "Jane Doe"). Drives the initials shown inside the
   * pill and, when `firstName` isn't supplied, also the colour calculation. */
  name?: string | null;
  /** First name used for the colour instead of `name`'s first word. */
  firstName?: string | null;
  /** Pixel diameter of the pill. Default 32 (matches the nav-bar avatar). */
  size?: number;
  /** Pixel font size for the initials. Default `Math.round(size * 0.5)`. */
  fontSize?: number;
  /** Renders the grey single-user silhouette (for "Unassigned" rows) instead
   * of personalised initials. `name` / `firstName` are ignored in this mode. */
  unassigned?: boolean;
  className?: string;
  style?: CSSProperties;
  /** Accessible label. Defaults to the user's name, or "Unassigned" when the
   * silhouette variant is shown. */
  'aria-label'?: string;
}

// Greys for the unassigned silhouette. Hard-coded hex rather than Tailwind tokens so the
// component renders in consumers without the fletchers theme, and `currentColor` works.
const UNASSIGNED_BG = '#E1E1E0'; // fletchers-gray-110
const UNASSIGNED_GLYPH = '#63666A'; // fletchers-gray-200

// Single-user silhouette, centred.
const UnassignedSilhouette = ({ pixelSize }: { pixelSize: number }) => (
  <svg
    width={pixelSize}
    height={pixelSize}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

export const UserAvatar = ({
  name,
  firstName,
  size = 32,
  fontSize,
  unassigned = false,
  className,
  style,
  'aria-label': ariaLabel,
}: UserAvatarProps) => {
  const baseClass = `inline-flex shrink-0 items-center justify-center rounded-full${className ? ` ${className}` : ''}`;

  if (unassigned) {
    return (
      <span
        role="img"
        aria-label={ariaLabel ?? 'Unassigned'}
        style={{
          width: size,
          height: size,
          backgroundColor: UNASSIGNED_BG,
          color: UNASSIGNED_GLYPH,
          ...style,
        }}
        className={baseClass}
      >
        <UnassignedSilhouette pixelSize={Math.round(size * 0.7)} />
      </span>
    );
  }

  const colors = getAvatarColorsFromName(firstName, name);
  // `?` when there's no name.
  const initials = getInitialsFromName(name ?? firstName) || '?';
  const resolvedFontSize = fontSize ?? Math.round(size * 0.5);

  return (
    <span
      role="img"
      aria-label={ariaLabel ?? name ?? firstName ?? ''}
      style={{
        width: size,
        height: size,
        backgroundColor: colors.backgroundColor,
        color: colors.color,
        fontSize: resolvedFontSize,
        lineHeight: 1,
        ...style,
      }}
      className={`${baseClass} font-semibold`}
    >
      {initials}
    </span>
  );
};
